import { motion } from "motion/react";
import { useState, useMemo, useEffect, ReactNode } from "react";
import InteractiveCalendar from "../InteractiveCalendar";
import {
  startOfMonth,
  startOfWeek,
  addDays,
  isSameDay,
  isSameMonth,
  format,
} from "date-fns";
import {
  EventFragment,
  ActivityFragment,
  PublicUserOrganization,
  OrganizationProjectsQuery,
} from "../../__generated__/graphql";
import ActivityCard from "../ActivityCard";
import EventPreview from "../EventPreview";
import { useQueryState, parseAsString } from "nuqs";
import UserSelectList from "../UserSelectList";
import ProjectSelectList from "../ProjectSelectList";
import { formatDateWithDay } from "../../lib/utils";

type Project = OrganizationProjectsQuery["projects"][number];

interface PlanningViewProps {
  // Data
  events: EventFragment[];
  activities: ActivityFragment[];
  loading?: boolean;
  error?: Error | null;

  // Filter options
  showProjectFilter?: boolean;
  participants?: PublicUserOrganization[]; // If provided, use this list for user filter instead of org members

  // Display options
  defaultDate?: Date | null;

  // External filter state (for dashboard context)
  selectedUsers?: PublicUserOrganization[];
  onSelectedUsersChange?: (users: PublicUserOrganization[]) => void;
  selectedProjects?: Project[];
  onSelectedProjectsChange?: (projects: Project[]) => void;

  // Custom filter slot (for project-specific participant list)
  customUserFilterSlot?: ReactNode;
  actions?: ReactNode;

  // Navigation callback for event details (used for in-page navigation via nuqs)
  onNavigateToEventDetails?: (event: EventFragment) => void;
}

export default function PlanningView({
  events,
  activities,
  loading = false,
  error = null,
  showProjectFilter = true,
  participants,
  defaultDate,
  selectedUsers: externalSelectedUsers,
  onSelectedUsersChange,
  selectedProjects: externalSelectedProjects,
  onSelectedProjectsChange,
  customUserFilterSlot,
  actions,
  onNavigateToEventDetails,
}: PlanningViewProps) {
  // Internal state for filters (used when external state is not provided)
  const [internalSelectedUsers, setInternalSelectedUsers] = useState<
    PublicUserOrganization[]
  >([]);
  const [internalSelectedProjects, setInternalSelectedProjects] = useState<
    Project[]
  >([]);
  const [selectedEventIds, setSelectedEventIds] = useState<Set<string>>(
    new Set()
  );

  // Use external state if provided, otherwise use internal state
  const selectedUsers = externalSelectedUsers ?? internalSelectedUsers;
  const setSelectedUsers = onSelectedUsersChange ?? setInternalSelectedUsers;
  const selectedProjects = externalSelectedProjects ?? internalSelectedProjects;
  const setSelectedProjects =
    onSelectedProjectsChange ?? setInternalSelectedProjects;

  // Handle event selection toggle
  const handleToggleEventSelect = (eventId: string) => {
    setSelectedEventIds((prev) => {
      const next = new Set(prev);
      if (next.has(eventId)) {
        next.delete(eventId);
      } else {
        next.add(eventId);
      }
      return next;
    });
  };

  // Custom parser for date-only format (YYYY-MM-DD) to avoid timezone issues
  const defaultDateString = defaultDate
    ? format(defaultDate, "yyyy-MM-dd")
    : format(new Date(), "yyyy-MM-dd");

  const parseAsDateOnly = parseAsString.withDefault(defaultDateString);
  const [dateString, setDateString] = useQueryState("date", parseAsDateOnly);

  // Convert date string to Date object (normalized to local midnight to avoid timezone issues)
  const selectedDate = useMemo(() => {
    if (!dateString) return defaultDate ?? new Date();
    // Parse YYYY-MM-DD and create date at local midnight
    const [year, month, day] = dateString.split("-").map(Number);
    return new Date(year, month - 1, day);
  }, [dateString, defaultDate]);

  // Initialize currentMonth from selectedDate (which may come from URL or default date)
  const [currentMonth, setCurrentMonth] = useState(
    selectedDate || defaultDate || new Date()
  );

  // Calculate interval for the visible month (6 weeks calendar view)
  const interval = useMemo(() => {
    const firstDayOfMonth = startOfMonth(currentMonth);
    const firstDayOfCalendar = startOfWeek(firstDayOfMonth, {
      weekStartsOn: 1,
    });
    const lastDayOfCalendar = addDays(firstDayOfCalendar, 41);

    return {
      start: firstDayOfCalendar,
      end: lastDayOfCalendar,
    };
  }, [currentMonth]);

  // Get selected project IDs
  const selectedProjectIds = useMemo(() => {
    return new Set(selectedProjects.map((project) => project.id));
  }, [selectedProjects]);

  // Create a map of eventId to isDraft status for easy lookup
  const eventDraftStatus = useMemo(() => {
    const map = new Map<string, boolean>();
    events.forEach((event) => {
      map.set(event.id, event.isDraft);
    });
    return map;
  }, [events]);

  // Get event IDs from selected projects (for filtering activities)
  const eventIdsFromSelectedProjects = useMemo(() => {
    if (selectedProjects.length === 0) return null;
    const eventIds = new Set<string>();
    events.forEach((event) => {
      if (event.projectId && selectedProjectIds.has(event.projectId)) {
        eventIds.add(event.id);
      }
    });
    return eventIds;
  }, [events, selectedProjects, selectedProjectIds]);

  // Filter events for calendar dots based on selected users and projects
  const filteredEventsForCalendar = useMemo(() => {
    let filtered = events;

    // Filter by selected projects if any are selected
    if (selectedProjects.length > 0) {
      filtered = filtered.filter((event) => {
        return event.projectId && selectedProjectIds.has(event.projectId);
      });
    }

    // Filter by selected users if any are selected
    if (selectedUsers.length > 0) {
      const selectedUserIds = new Set(selectedUsers.map((user) => user.id));
      filtered = filtered.filter((event) => {
        return event.eventParticipants?.some(
          (participant) =>
            participant?.publicUserOrganization &&
            selectedUserIds.has(participant.publicUserOrganization.id)
        );
      });
    }

    // Filter events within the calendar interval
    filtered = filtered.filter((event) => {
      const eventDate = new Date(event.startDate);
      return eventDate >= interval.start && eventDate <= interval.end;
    });

    return filtered;
  }, [events, selectedUsers, selectedProjects, selectedProjectIds, interval]);

  const filteredActivitiesForCalendar = useMemo(() => {
    let filtered = activities;

    // Filter by selected projects if any are selected (through events or direct project link)
    if (eventIdsFromSelectedProjects !== null) {
      filtered = filtered.filter((activity) => {
        // Include activities linked to events from selected projects
        if (
          activity.eventId &&
          eventIdsFromSelectedProjects.has(activity.eventId)
        ) {
          return true;
        }
        // Include activities linked directly to selected projects
        if (activity.projectId && selectedProjectIds.has(activity.projectId)) {
          return true;
        }
        return false;
      });
    }

    // Filter by selected users if any are selected
    if (selectedUsers.length > 0) {
      const selectedUserIds = new Set(selectedUsers.map((user) => user.id));
      filtered = filtered.filter((activity) => {
        return activity.activityParticipants?.some(
          (participant) =>
            participant?.publicUserOrganization &&
            selectedUserIds.has(participant.publicUserOrganization.id)
        );
      });
    }

    // Filter activities within the calendar interval
    filtered = filtered.filter((activity) => {
      const activityDate = new Date(activity.startDate);
      return activityDate >= interval.start && activityDate <= interval.end;
    });

    return filtered;
  }, [
    activities,
    selectedUsers,
    eventIdsFromSelectedProjects,
    selectedProjectIds,
    interval,
  ]);

  // Update currentMonth when selectedDate changes (e.g., from URL)
  useEffect(() => {
    if (selectedDate && !isSameMonth(selectedDate, currentMonth)) {
      setCurrentMonth(selectedDate);
    }
  }, [selectedDate, currentMonth]);

  const handleDaySelect = (date: Date) => {
    // Convert Date to YYYY-MM-DD format (date-only, no timezone issues)
    setDateString(format(date, "yyyy-MM-dd"));
  };

  const handleMonthChange = (month: Date) => {
    setCurrentMonth(month);
    // Convert Date to YYYY-MM-DD format
    setDateString(format(startOfMonth(month), "yyyy-MM-dd"));
  };

  // Get all events for the selected day (unfiltered)
  const allSelectedDayEvents = useMemo(() => {
    if (!selectedDate) return [];

    let filtered = events.filter((event) => {
      const eventDate = new Date(event.startDate);
      return isSameDay(eventDate, selectedDate);
    });

    // Filter by selected projects if any are selected (only for display, not opacity)
    if (selectedProjects.length > 0) {
      filtered = filtered.filter((event) => {
        return event.projectId && selectedProjectIds.has(event.projectId);
      });
    }

    return filtered;
  }, [events, selectedDate, selectedProjects, selectedProjectIds]);

  // Get all activities for the selected day (unfiltered), sorted chronologically
  const allSelectedDayActivities = useMemo(() => {
    if (!selectedDate) return [];

    let filtered = activities.filter((activity) => {
      const activityDate = new Date(activity.startDate);
      return isSameDay(activityDate, selectedDate);
    });

    // Filter by selected projects if any are selected (through events or direct project link)
    if (eventIdsFromSelectedProjects !== null) {
      filtered = filtered.filter((activity) => {
        // Include activities linked to events from selected projects
        if (
          activity.eventId &&
          eventIdsFromSelectedProjects.has(activity.eventId)
        ) {
          return true;
        }
        // Include activities linked directly to selected projects
        if (activity.projectId && selectedProjectIds.has(activity.projectId)) {
          return true;
        }
        return false;
      });
    }

    return filtered.sort((a, b) => {
      return new Date(a.startDate).getTime() - new Date(b.startDate).getTime();
    });
  }, [
    activities,
    selectedDate,
    eventIdsFromSelectedProjects,
    selectedProjectIds,
  ]);

  // Check if any filter is active
  const hasUserFilter = selectedUsers.length > 0;
  const hasEventFilter = selectedEventIds.size > 0;
  const hasAnyFilter = hasUserFilter || hasEventFilter;

  // Get IDs of events that match the current user filter
  const matchingEventIds = useMemo(() => {
    if (!hasUserFilter) return null; // null means no filter active

    const selectedUserIds = new Set(selectedUsers.map((user) => user.id));
    const matchingIds = new Set<string>();

    allSelectedDayEvents.forEach((event) => {
      const matches = event.eventParticipants?.some(
        (participant) =>
          participant?.publicUserOrganization &&
          selectedUserIds.has(participant.publicUserOrganization.id)
      );
      if (matches) {
        matchingIds.add(event.id);
      }
    });

    return matchingIds;
  }, [allSelectedDayEvents, selectedUsers, hasUserFilter]);

  // Get IDs of activities that match the current filters (user and event)
  const matchingActivityIds = useMemo(() => {
    if (!hasAnyFilter) return null; // null means no filter active

    const matchingIds = new Set<string>();

    allSelectedDayActivities.forEach((activity) => {
      let matchesUser = true;
      let matchesEvent = true;

      // Check user filter
      if (hasUserFilter) {
        const selectedUserIds = new Set(selectedUsers.map((user) => user.id));
        matchesUser =
          activity.activityParticipants?.some(
            (participant) =>
              participant?.publicUserOrganization &&
              selectedUserIds.has(participant.publicUserOrganization.id)
          ) ?? false;
      }

      // Check event filter
      if (hasEventFilter) {
        matchesEvent = activity.eventId
          ? selectedEventIds.has(activity.eventId)
          : false;
      }

      // Activity must match both filters (if they are active)
      if (matchesUser && matchesEvent) {
        matchingIds.add(activity.id);
      }
    });

    return matchingIds;
  }, [
    allSelectedDayActivities,
    selectedUsers,
    selectedEventIds,
    hasUserFilter,
    hasEventFilter,
    hasAnyFilter,
  ]);

  // Helper to check if an event matches the filter
  const isEventMatching = (eventId: string) => {
    if (matchingEventIds === null) return true; // No filter active
    return matchingEventIds.has(eventId);
  };

  // Helper to check if an activity matches the filter
  const isActivityMatching = (activityId: string) => {
    if (matchingActivityIds === null) return true; // No filter active
    return matchingActivityIds.has(activityId);
  };

  return (
    <motion.div
      className="flex flex-col gap-4 text-primary"
      data-testid="planning-view"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="flex gap-4 flex-1 min-h-0">
        {/* Small interactive calendar on the top left */}
        <div className="w-fit flex-shrink-0 flex flex-col gap-4">
          <InteractiveCalendar
            onDaySelect={handleDaySelect}
            selectedDate={selectedDate}
            events={filteredEventsForCalendar}
            activities={filteredActivitiesForCalendar}
            currentMonth={currentMonth}
            onMonthChange={handleMonthChange}
          />

          {/* User filter - either custom slot or default UserSelectList */}
          {customUserFilterSlot ? (
            customUserFilterSlot
          ) : (
            <UserSelectList
              onSelect={setSelectedUsers}
              participants={participants}
              className="w-[270px] flex-shrink-0 border border-input rounded-md p-2"
            />
          )}

          {/* Project filter - only shown if enabled */}
          {showProjectFilter && (
            <ProjectSelectList
              onSelect={setSelectedProjects}
              className="w-[270px] flex-shrink-0 border border-input rounded-md p-2"
            />
          )}
        </div>

        {/* Selected day events */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -30 }}
          transition={{ duration: 0.2 }}
          key={selectedDate?.toISOString()}
          className="flex-1 overflow-y-auto custom-scrollbar min-h-0"
        >
          <div className="flex flex-col gap-4">
            <div className="flex justify-between items-center">
              <div className="text-lg font-bold text-primary">
                {formatDateWithDay(selectedDate)}
              </div>
              {actions}
            </div>
            <div className="h-px bg-border" />

            {loading ? (
              <div className="text-secondary">Chargement...</div>
            ) : error ? (
              <div className="text-secondary">Erreur: {error.message}</div>
            ) : allSelectedDayEvents.length === 0 &&
              allSelectedDayActivities.length === 0 ? (
              <div className="text-tertiary text-sm">
                Aucun événement ou activité ce jour
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {/* Events section */}
                {allSelectedDayEvents.length > 0 && (
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-semibold text-secondary">
                        Événements du jour
                      </h3>
                      {selectedEventIds.size > 0 && (
                        <button
                          onClick={() => setSelectedEventIds(new Set())}
                          className="text-xs text-accent hover:text-accent/80 underline cursor-pointer"
                        >
                          Réinitialiser le filtre ({selectedEventIds.size})
                        </button>
                      )}
                    </div>
                    <div className="flex flex-row gap-3 overflow-x-auto custom-scrollbar">
                      {allSelectedDayEvents.map((event) => {
                        const isMatching = isEventMatching(event.id);
                        const isSelected = selectedEventIds.has(event.id);
                        return (
                          <motion.div
                            className="w-xs flex-shrink-0"
                            key={event.id}
                            initial={false}
                            animate={{
                              opacity: isMatching ? 1 : 0.3,
                              scale: isMatching ? 1 : 0.98,
                            }}
                            transition={{ duration: 0.3, ease: "easeInOut" }}
                          >
                            <EventPreview
                              event={event}
                              selectable={true}
                              isSelected={isSelected}
                              onToggleSelect={handleToggleEventSelect}
                              onNavigateToDetails={onNavigateToEventDetails}
                            />
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Activities section */}
                {allSelectedDayActivities.length > 0 ? (
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-semibold text-secondary">
                        Activités du jour
                      </h3>
                      {hasEventFilter && (
                        <span className="text-xs text-tertiary">
                          (filtrées par {selectedEventIds.size} événement
                          {selectedEventIds.size > 1 ? "s" : ""})
                        </span>
                      )}
                    </div>
                    {allSelectedDayActivities.map((activity) => {
                      const isMatching = isActivityMatching(activity.id);
                      const isDraft = activity.eventId
                        ? eventDraftStatus.get(activity.eventId) ?? false
                        : false;
                      return (
                        <motion.div
                          key={activity.id}
                          initial={false}
                          animate={{
                            opacity: isMatching ? 1 : 0.3,
                            scale: isMatching ? 1 : 0.99,
                          }}
                          transition={{ duration: 0.3, ease: "easeInOut" }}
                        >
                          <ActivityCard activity={activity} isDraft={isDraft} />
                        </motion.div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-tertiary text-sm">
                    Aucune activité ce jour
                  </div>
                )}
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}
